/**
 * Capture workflow persistence (ported from Noteble — ADR-022).
 *
 * Serializes/deserializes the workflow context to/from localStorage so a
 * refresh resumes where the user was. Only serializable fields are stored —
 * photo blobs live in IndexedDB (AddContext / drafts). Captured voice
 * transcripts ARE included (they are plain text) so a refresh never loses a
 * recording.
 *
 * Single storage key, 30-minute expiry.
 */

import type { TemplateMetadata } from '@/services/templatesApi';
import type { CapturedStream, InputMethod, WorkflowContext, WorkflowStep } from './types';
import { GENERIC_TEMPLATE } from './genericTemplate';
import { createLogger } from '@/lib/logger';

const log = createLogger('capture/workflow-persistence');

const STORAGE_KEY = 'sdx_capture_workflow_state';
const EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

interface PersistedWorkflow {
  step: WorkflowStep;
  inputMethod: string;
  note: string;
  capturedStreams: CapturedStream[];
  selectedTemplateIds: string[];
  activeDraftId: string | null;
  timestamp: number;
}

export function serializeWorkflow(ctx: WorkflowContext): void {
  const nothingWorthSaving =
    ctx.step === 'capture'
    && !ctx.note.trim()
    && ctx.capturedStreams.length === 0
    && ctx.selectedTemplates.length === 0;

  try {
    if (nothingWorthSaving) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    const data: PersistedWorkflow = {
      step: ctx.step,
      inputMethod: ctx.inputMethod,
      note: ctx.note,
      capturedStreams: ctx.capturedStreams,
      selectedTemplateIds: ctx.selectedTemplates.map((t) => t.id),
      activeDraftId: ctx.activeDraftId,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (err) {
    log.warn('Failed to serialize workflow', { error: err instanceof Error ? err.message : 'Unknown' });
  }
}

export interface DeserializedWorkflow {
  context: Partial<WorkflowContext>;
  /** Template IDs that couldn't be resolved (templates not loaded yet). */
  pendingTemplateIds: string[];
}

/**
 * Load a persisted workflow and resolve template references against the
 * loaded template list. Unresolved ids are deferred via pendingTemplateIds.
 */
export function deserializeWorkflow(
  templates: TemplateMetadata[],
): DeserializedWorkflow | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw) as PersistedWorkflow;

    if (Date.now() - data.timestamp > EXPIRY_MS) {
      clearPersistedWorkflow();
      return null;
    }

    const selectedTemplates: TemplateMetadata[] = [];
    const pendingTemplateIds: string[] = [];
    for (const id of data.selectedTemplateIds || []) {
      if (id === GENERIC_TEMPLATE.id) {
        selectedTemplates.push(GENERIC_TEMPLATE);
      } else {
        const t = templates.find((tmpl) => tmpl.id === id);
        if (t) selectedTemplates.push(t);
        else pendingTemplateIds.push(id);
      }
    }

    const context: Partial<WorkflowContext> = {
      step: data.step,
      inputMethod: data.inputMethod as InputMethod,
      note: data.note || '',
      capturedStreams: Array.isArray(data.capturedStreams) ? data.capturedStreams : [],
      selectedTemplates,
      activeDraftId: data.activeDraftId ?? null,
    };

    log.info('Workflow deserialized', { step: data.step, pendingTemplates: pendingTemplateIds.length });
    return { context, pendingTemplateIds };
  } catch (err) {
    log.warn('Failed to deserialize workflow', { error: err instanceof Error ? err.message : 'Unknown' });
    return null;
  }
}

export function clearPersistedWorkflow(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort
  }
}
